/*
 * Keeping the sign-in fresh without making a page wait on it.
 *
 * Ported from the mobile app (nysmah-mobile/src/redux/api/tokenRefresh.js),
 * which met the same morning: the access token lasts a day, so the first
 * request after a night went out expired, came back 401, and only then
 * renewed. In the dashboard every request on the page renewed on its own,
 * and that burst is what blanked the sidebar — see redux/api/index.js.
 *
 *  - a token about to expire is renewed BEFORE the request is sent;
 *  - every caller shares one renewal (`singleFlight`): a page that fires
 *    seven requests renews once;
 *  - only the server refusing the refresh token ends the session. A timeout,
 *    a rate limit or a dropped connection fails that one request and leaves
 *    the admin signed in.
 */

// Renew when less than this is left, so a request never reaches the server
// with a token that expires on the way.
const REFRESH_WINDOW_MS = 60 * 1000;

/** The token's expiry in epoch ms, or null if it cannot be read. */
export function tokenExpiry(token) {
  try {
    const payload = String(token).split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const exp = JSON.parse(atob(padded)).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Whether `token` expires within the refresh window (or already has). */
export function isExpiringSoon(token, now = Date.now()) {
  const expiry = tokenExpiry(token);
  return expiry !== null && expiry - now < REFRESH_WINDOW_MS;
}

/**
 * Whether a failed renewal means the session is over (sign in again), as
 * opposed to a network problem worth retrying later.
 */
export function isSessionRejected(error) {
  if (error?.sessionRejected) {
    return true;
  }
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

/**
 * Wrap `fn` so concurrent callers share one in-flight call. The arguments
 * of the call that starts the flight are the ones `fn` sees; later callers
 * join that flight as it is.
 */
export function singleFlight(fn) {
  let pending = null;
  return (...args) => {
    if (!pending) {
      pending = Promise.resolve()
        .then(() => fn(...args))
        .finally(() => {
          pending = null;
        });
    }
    return pending;
  };
}
